import { useState, useEffect, useRef } from 'preact/hooks';
import { Container, Stack, Title, ScrollArea, Text, Box, Paper, Group, Badge, Avatar, ActionIcon, Tooltip, Indicator } from '@mantine/core';
import { IconUser, IconBrandGithub, IconPhoto, IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { ChatStatus } from './ChatStatus';
import { AISettings, AIConfig, DEFAULT_AI_CONFIG } from './AISettings';
import { 
  checkChromeAIAvailability, 
  createChatSession, 
  sendMessage, 
  destroySession,
  createChatMessage,
  cleanupImagePreviews,
  AISessionConfig
} from '@/lib/chrome-ai';
import { ChromeAIStatus, LanguageModelSession, ChatMessage as ChatMessageType, ImageFile } from '@/types/chrome-ai';

interface QueuedMessage {
  id: string;
  content: string;
  timestamp: Date;
  images?: ImageFile[];
}

export function ChatApp() {
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [aiStatus, setAiStatus] = useState<ChromeAIStatus>({
    available: false,
    status: 'Checking...',
    promptApiAvailable: false,
    languageModelAvailable: false,
  });
  const [session, setSession] = useState<LanguageModelSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [aiConfig, setAiConfig] = useState<AIConfig>(DEFAULT_AI_CONFIG);
  const [messageQueue, setMessageQueue] = useState<QueuedMessage[]>([]);
  const [processing, setProcessing] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const processingRef = useRef(false);
  const queueRef = useRef<QueuedMessage[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const inputAreaRef = useRef<HTMLDivElement>(null);

  // Check Chrome AI availability on mount
  useEffect(() => {
    checkAIAvailability();
  }, []);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollArea = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollArea) {
        scrollArea.scrollTop = scrollArea.scrollHeight;
      }
    }
  }, [messages]);

  // Scroll to input field when files are dropped
  useEffect(() => {
    if (droppedFiles.length > 0) {
      // Wait a bit for the upload section to open
      setTimeout(() => {
        // Try scrolling the main container to bottom
        window.scrollTo({ 
          top: document.documentElement.scrollHeight, 
          behavior: 'smooth' 
        });
        
        // Also try scrolling the input area into view
        if (inputAreaRef.current) {
          inputAreaRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'end',
            inline: 'nearest'
          });
        }
      }, 300);
    }
  }, [droppedFiles]);

  // Initialize AI session when available or config changes
  useEffect(() => {
    if (aiStatus.available && !session) {
      initializeSession();
    }
  }, [aiStatus.available, aiConfig]);

  // Cleanup session on unmount
  useEffect(() => {
    return () => {
      if (session) {
        destroySession(session);
      }
      // Clear queue on unmount and cleanup image previews
      messageQueue.forEach(msg => {
        if (msg.images) {
          cleanupImagePreviews(msg.images);
        }
      });
      setMessageQueue([]);
      queueRef.current = [];
    };
  }, [session]);

  const checkAIAvailability = async () => {
    setCheckingStatus(true);
    try {
      const status = await checkChromeAIAvailability();
      setAiStatus(status);
    } catch (error) {
      console.error('Error checking AI availability:', error);
      setAiStatus({
        available: false,
        status: 'Error checking availability',
        instructions: error instanceof Error ? error.message : 'Unknown error',
        promptApiAvailable: false,
        languageModelAvailable: false,
      });
    } finally {
      setCheckingStatus(false);
    }
  };

  const initializeSession = async () => {
    if (!aiStatus.available) return;

    try {
      setLoading(true);
      
      // Convert AIConfig to AISessionConfig
      const sessionConfig: AISessionConfig = {
        systemPrompt: aiConfig.systemPrompt,
        enableMultimodal: true, // Always enable multimodal support
      };
      
      if (aiConfig.useAdvancedSettings && aiConfig.temperature !== undefined && aiConfig.topK !== undefined) {
        sessionConfig.temperature = aiConfig.temperature;
        sessionConfig.topK = aiConfig.topK;
      }
      
      const newSession = await createChatSession(sessionConfig);
      setSession(newSession);
    } catch (error) {
      console.error('Error initializing AI session:', error);
      setAiStatus(prev => ({
        ...prev,
        available: false,
        status: 'Failed to initialize AI session',
        instructions: error instanceof Error ? error.message : 'Unknown error',
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (userMessage: string, images?: ImageFile[]) => {
    if (!session) return;

    // Trim the message to prevent empty/whitespace-only messages
    const trimmedMessage = userMessage.trim();
    if (!trimmedMessage && (!images || images.length === 0)) return;

    // Check for duplicate messages in queue (only check text, not images)
    const isDuplicate = messageQueue.some(queuedMsg => queuedMsg.content === trimmedMessage);
    if (isDuplicate && (!images || images.length === 0)) {
      return; // Don't add duplicate messages to queue
    }

    // Create queued message with images
    const queuedMessage: QueuedMessage = {
      id: `queue_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      content: trimmedMessage,
      timestamp: new Date(),
      images: images,
    };

    // Add to queue first (don't add to messages yet - we'll add them in order during processing)
    setMessageQueue(prev => {
      const newQueue = [...prev, queuedMessage];
      queueRef.current = newQueue;
      return newQueue;
    });

    // Start processing if not already processing
    if (!processingRef.current) {
      processQueue();
    }
  };

  const processQueue = async () => {
    if (processingRef.current || !session) return;

    processingRef.current = true;
    setProcessing(true);

    // Process messages until queue is empty using ref
    while (queueRef.current.length > 0) {
      setLoading(true);
      
      // Get the first message from the ref queue
      const queuedMessage = queueRef.current[0];
      
      // Remove it from both ref and state
      queueRef.current = queueRef.current.slice(1);
      setMessageQueue(queueRef.current);

      // Add user message to chat now (in correct order)
      const userMsg = createChatMessage('user', queuedMessage.content, queuedMessage.images);
      setMessages(prev => [...prev, userMsg]);

      try {
        // Get AI response for THIS specific message with images
        const aiResponse = await sendMessage(session, queuedMessage.content, queuedMessage.images);
        
        // Add AI response to chat immediately after user message
        const aiMsg = createChatMessage('assistant', aiResponse);
        setMessages(prev => [...prev, aiMsg]);
      } catch (error) {
        console.error('Error processing queued message:', error);
        
        // Add error message
        const errorMsg = createChatMessage(
          'assistant', 
          `Sorry, I encountered an error processing your message: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
        setMessages(prev => [...prev, errorMsg]);
      }

      setLoading(false);

      // Small delay between messages to prevent overwhelming the AI
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setProcessing(false);
    processingRef.current = false;
  };

  // Process queue when it changes and we're not already processing
  useEffect(() => {
    if (queueRef.current.length > 0 && !processingRef.current && session) {
      processQueue();
    }
  }, [messageQueue.length, session]);

  const clearQueue = () => {
    // Clean up image previews in queue
    messageQueue.forEach(msg => {
      if (msg.images) {
        cleanupImagePreviews(msg.images);
      }
    });
    
    setMessageQueue([]);
    queueRef.current = [];
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + K to clear queue
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        if (messageQueue.length > 0) {
          clearQueue();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [messageQueue.length]);

  const handleRetryStatus = () => {
    checkAIAvailability();
  };

  const handleConfigChange = (newConfig: AIConfig) => {
    setAiConfig(newConfig);
    
    // If we have an active session, destroy it so it gets recreated with new config
    if (session) {
      destroySession(session);
      setSession(null);
    }
    
    // Clear any pending messages in queue
    clearQueue();
  };

  const handleConfigReset = () => {
    if (session) {
      destroySession(session);
      setSession(null);
    }
    setMessages([]);
    // Clear any pending messages in queue
    clearQueue();
  };

  // Drag and drop handlers for entire chat area
  const handleDragEnter = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.types.includes('Files')) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if leaving the container entirely
    const target = e.currentTarget as HTMLElement;
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!target.contains(relatedTarget)) {
      setDragActive(false);
    }
  };

  const handleDragOver = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (!aiStatus.available) return;

    const files = Array.from(e.dataTransfer?.files || []) as File[];
    if (files.length > 0) {
      setDroppedFiles(files);
    }
  };

  const handleFilesProcessed = () => {
    // Clear the dropped files after they've been processed
    setDroppedFiles([]);
  };

  return (
    <Container 
      size="xl" 
      style={{ 
        height: '100vh', 
        display: 'flex', 
        flexDirection: 'column', 
        maxWidth: '1400px',
        padding: '0.5rem'
      }}
    >
      <Stack style={{ height: '100%', minHeight: 0 }} gap="xs">
        {/* Header */}
        <Box style={{ flexShrink: 0 }}>
          <Group justify="space-between" align="center" wrap="nowrap">
            <Box style={{ minWidth: 0, flex: 1 }}>
              <Group gap="xs" align="center" wrap="nowrap">
                <Title order={3} style={{ margin: 0, fontSize: '1.25rem' }}>
                  ChatNano
                </Title>
                <Tooltip label="View on GitHub" position="right">
                  <ActionIcon
                    component="a"
                    href="https://github.com/papayaah/chatnano"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="subtle"
                    color="gray"
                    size="sm"
                  >
                    <IconBrandGithub size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              <Group gap="xs" align="center" mt={4} wrap="wrap">
                <Group gap="xs" align="center">
                  <Indicator 
                    color={aiStatus.available ? 'green' : 'red'} 
                    size={8}
                    withBorder
                  >
                    <Text size="xs" c="dimmed">
                      Built-in Chrome AI
                    </Text>
                  </Indicator>
                  <Badge
                    size="xs"
                    variant="gradient"
                    gradient={{ from: '#4285f4', to: '#34a853', deg: 45 }}
                    style={{
                      fontWeight: 500,
                      fontSize: '10px',
                      textTransform: 'none',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Gemini Nano
                  </Badge>
                  {aiStatus.available && (
                    <Badge
                      size="xs"
                      variant="gradient"
                      gradient={{ from: '#ea4335', to: '#fbbc04', deg: 45 }}
                      style={{
                        fontWeight: 500,
                        fontSize: '9px',
                        textTransform: 'none',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Multimodal
                    </Badge>
                  )}
                  {aiStatus.available && (
                    <Tooltip label="AI Ready">
                      <IconCheck size={12} color="green" />
                    </Tooltip>
                  )}
                  {!aiStatus.available && (
                    <Tooltip label="AI Not Available">
                      <IconAlertCircle size={12} color="red" />
                    </Tooltip>
                  )}
                </Group>
                {messageQueue.length > 0 && (
                  <Badge 
                    size="xs" 
                    color="blue" 
                    variant="light"
                    style={{ cursor: 'pointer' }}
                    onClick={clearQueue}
                    title="Click to clear queue"
                  >
                    {messageQueue.length} queued
                  </Badge>
                )}
                {processing && (
                  <Badge size="xs" color="green" variant="light">
                    Processing...
                  </Badge>
                )}
              </Group>
            </Box>
            <AISettings
              config={aiConfig}
              onChange={handleConfigChange}
              onReset={handleConfigReset}
            />
          </Group>
        </Box>

        {/* Debug Panel - Commented out for production. Uncomment if needed for debugging */}
        {/* <AIDebugPanel /> */}

        {/* Show status alert only when AI is not available and not checking */}
        {!aiStatus.available && !checkingStatus && (
          <ChatStatus 
            status={aiStatus} 
            onRetry={handleRetryStatus} 
            loading={checkingStatus} 
          />
        )}

        {/* Chat Messages */}
        <Paper 
          withBorder 
          radius="md" 
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: 0,
            position: 'relative'
          }}
        >
          {/* Drag overlay */}
          {dragActive && (
            <Box
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(34, 139, 230, 0.1)',
                border: '3px dashed #228be6',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                pointerEvents: 'none'
              }}
            >
              <Paper p="xl" radius="md" style={{ backgroundColor: 'white' }}>
                <Stack align="center" gap="sm">
                  <IconPhoto size={48} color="#228be6" />
                  <Text size="lg" fw={600} c="blue">
                    Drop files here
                  </Text>
                  <Text size="sm" c="dimmed">
                    Upload images and files to analyze
                  </Text>
                </Stack>
              </Paper>
            </Box>
          )}
          <ScrollArea 
            ref={scrollAreaRef}
            style={{ flex: 1, minHeight: 0 }} 
            p="md"
            scrollbarSize={6}
          >
            <Stack gap="sm">
              {messages.length === 0 && aiStatus.available && (
                <Box ta="center" py="xl">
                  <Text c="dimmed">
                    Start a conversation by typing a message below!
                  </Text>
                </Box>
              )}
              
              {messages.map((message) => (
                <ChatMessage key={message.id} message={message} />
              ))}
              
              {/* Show queued messages */}
              {messageQueue.map((queuedMsg, index) => (
                <Group
                  key={queuedMsg.id}
                  align="flex-start"
                  gap="sm"
                  style={{
                    flexDirection: 'row-reverse',
                    marginBottom: '1rem',
                  }}
                >
                  <Avatar
                    size="sm"
                    radius="xl"
                    color="blue"
                    style={{ flexShrink: 0, opacity: 0.6 }}
                  >
                    <IconUser size={16} />
                  </Avatar>
                  
                  <Paper
                    p="md"
                    radius="lg"
                    style={{
                      maxWidth: queuedMsg.content.length > 200 ? '90%' : '70%',
                      backgroundColor: 'var(--mantine-color-blue-light)',
                      opacity: 0.6,
                      marginLeft: 'auto',
                      marginRight: '0',
                      border: '2px dashed var(--mantine-color-blue-4)'
                    }}
                  >
                    <Group gap="xs" align="center" mb="xs">
                      <Badge size="xs" color="blue" variant="outline">
                        #{index + 1} in queue
                      </Badge>
                      {queuedMsg.images && queuedMsg.images.length > 0 && (
                        <Badge size="xs" color="green" variant="outline">
                          {queuedMsg.images.length} image{queuedMsg.images.length !== 1 ? 's' : ''}
                        </Badge>
                      )}
                    </Group>
                    <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
                      {queuedMsg.content || 'Analyzing images...'}
                    </Text>
                    <Text
                      size="xs"
                      c="dimmed"
                      mt="xs"
                      ta="right"
                    >
                      {queuedMsg.timestamp.toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })} (queued)
                    </Text>
                  </Paper>
                </Group>
              ))}
              
              {loading && (
                <Box style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Paper 
                    p="md" 
                    radius="lg"
                    style={{ backgroundColor: 'var(--mantine-color-gray-light)' }}
                  >
                    <Group gap="xs" align="center">
                      <Text size="sm" c="dimmed">
                        AI is typing...
                      </Text>
                      {messageQueue.length > 0 && (
                        <Badge size="xs" color="green" variant="light">
                          Processing queue
                        </Badge>
                      )}
                    </Group>
                  </Paper>
                </Box>
              )}
            </Stack>
          </ScrollArea>

          {/* Chat Input */}
          <Box 
            ref={inputAreaRef}
            p="md" 
            style={{ 
              borderTop: '1px solid var(--mantine-color-gray-3)',
              flexShrink: 0,
              maxHeight: '40vh',
              overflow: 'auto'
            }}
          >
            <ChatInput
              onSendMessage={handleSendMessage}
              disabled={!aiStatus.available}
              placeholder={
                aiStatus.available 
                  ? messageQueue.length > 0 
                    ? `Type your message (${messageQueue.length} in queue)...` 
                    : "Type your message..."
                  : "AI not available - check status above"
              }
              externalFiles={droppedFiles.length > 0 ? droppedFiles : undefined}
              onExternalFilesProcessed={handleFilesProcessed}
            />
            {messageQueue.length > 0 && (
              <Group justify="center" mt="xs">
                <Text size="xs" c="dimmed">
                  {messageQueue.length} message{messageQueue.length !== 1 ? 's' : ''} in queue • 
                  Processing one by one
                </Text>
                <Text 
                  size="xs" 
                  c="blue" 
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={clearQueue}
                >
                  Clear Queue
                </Text>
              </Group>
            )}
          </Box>
        </Paper>
      </Stack>
    </Container>
  );
}